import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import Swal from 'sweetalert2';
import { AuthContext } from '../App';
import envConfig from '../envConfig';
import Footer from './Footer';
import { RecipientGroupCard, RecipientModal } from './RecipientPicker';

// ── design tokens (mirrors blue_resilience_map 3.html) ───────────────────────
const T = {
  navy:   '#12324f',
  navy2:  '#1c4467',
  ink:    '#1f2a33',
  muted:  '#6b7a86',
  line:   '#e1e6ea',
  panel:  '#f6f8f9',
  field:  '#eef1f3',
};

// ── constants ─────────────────────────────────────────────────────────────────
const RISK_COLORS  = { EMERGENCY: '#d64a3e', 'HIGH RISK': '#e2793a', 'INTERMEDIATE RISK': '#e2a53a', NORMAL: '#0fb4c4' };
const RISK_TEXT    = { EMERGENCY: '#fff',    'HIGH RISK': '#fff',    'INTERMEDIATE RISK': '#4a3200',  NORMAL: '#04424c' };
const COUNTRY_VIEW = { 1: { center: [-13.2543, 34.3015], zoom: 7 }, 2: { center: [-13.1339, 27.8493], zoom: 6 } };
const SPECIES_IMG  = { 1: '1-0', 2: '1-1', 3: '2-1', 4: '2-0' };
const DEFAULT_HOUR = 9;
const DATA_SOURCES = [
  { key: 'vc', label: 'Visual Crossing' },
  { key: 'ow', label: 'OpenWeather' },
];

// ── shared style objects ──────────────────────────────────────────────────────
const S = {
  blockLabel: { fontSize: 10.5, fontWeight: 700, color: T.muted, letterSpacing: '.04em', textTransform: 'uppercase', margin: '0 0 8px' },
  metaKey:    { fontSize: 10.5, color: T.muted, marginBottom: 2 },
  metaVal:    { fontSize: 13.5, fontWeight: 700, color: T.ink },
  chartCard:  { border: `1px solid ${T.line}`, borderRadius: 8, padding: '12px 14px 10px', marginBottom: 14, background: '#fff' },
  chartTitle: { fontSize: 12, fontWeight: 700, color: T.ink, marginBottom: 6, display: 'flex', justifyContent: 'space-between' },
  statCard:   { border: `1px solid ${T.line}`, borderRadius: 8, padding: 12, background: '#fff' },
};

// ── helpers ───────────────────────────────────────────────────────────────────
const riskColor  = (level) => RISK_COLORS[level] || '#9e9e9e';
const offsetDate = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0]; };

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

// riskOrder: array of weekly_risk_level strings in evaluation_order from DB
function worstRiskFromRecords(records, keyField, riskOrder) {
  const map = {};
  const idx = (l) => { const i = riskOrder.indexOf(l); return i === -1 ? 99 : i; };
  for (const r of records) {
    const id = r[keyField];
    if (!map[id] || idx(r.risk_level) < idx(map[id])) map[id] = r.risk_level;
  }
  return map;
}

// ── sub-component: right panel ────────────────────────────────────────────────
function CampPanel({ detail, loading, periodRiskLevel }) {
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
      <div className="spinner-border" style={{ color: T.navy2 }} />
    </div>
  );
  if (!detail) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, color: T.muted, padding: 24 }}>
      <i className="bi bi-map" style={{ fontSize: 36, opacity: 0.4, marginBottom: 10 }} />
      <p style={{ fontSize: 13, textAlign: 'center', margin: 0 }}>Click a camp on the map<br />or apply a selection to view details</p>
    </div>
  );

  const { camp, conditions, outlook, farmers_count, officers_count, species: speciesObj } = detail;
  const ol = outlook || [];

  const dates          = ol.map(x => fmtDate(x.date));
  const pondSeries     = ol.map(x => x.predicted_pond_temp ?? null);
  const airMinSeries   = ol.map(x => x.temp_min ?? null);
  const airMaxSeries   = ol.map(x => x.temp_max ?? null);
  const rainfallSeries = ol.map(x => x.rainfall ?? null);
  const humiditySeries = ol.map(x => x.humidity ?? null);
  const hasRainfall    = rainfallSeries.some(v => v !== null);
  const hasHumidity    = humiditySeries.some(v => v !== null);

  const chartBase = (height = 130) => ({
    chart:   { height, margin: [18, 8, 28, 36], style: { fontFamily: '"Segoe UI",system-ui,sans-serif' } },
    title:   { text: '' },
    credits: { enabled: false },
    legend:  { enabled: false },
    xAxis:   { categories: dates, labels: { style: { fontSize: '9px', color: T.muted } }, lineColor: T.line, tickColor: T.line },
    tooltip: { shared: true },
  });

  const yAxis = (label, extra = {}) => ({
    title: { text: label, style: { fontSize: '9px', color: T.muted } },
    labels: { style: { fontSize: '9px', color: T.muted } },
    gridLineColor: T.line,
    ...extra,
  });

  const pondChartOpts = {
    ...chartBase(),
    chart: { ...chartBase().chart, type: 'line' },
    yAxis: yAxis('°C'),
    series: [{ name: 'Pond Temp', data: pondSeries, color: T.navy, lineWidth: 2,
               marker: { radius: 4, fillColor: '#fff', lineWidth: 2, lineColor: T.navy } }],
    tooltip: { valueSuffix: ' °C' },
  };

  const airChartOpts = {
    ...chartBase(),
    chart: { ...chartBase().chart, type: 'line' },
    yAxis: yAxis('°C'),
    legend: { enabled: true, itemStyle: { fontSize: '10px' } },
    series: [
      { name: 'Max', data: airMaxSeries, color: '#e2793a', lineWidth: 2, marker: { radius: 3 } },
      { name: 'Min', data: airMinSeries, color: '#3aa0c9', lineWidth: 2, marker: { radius: 3 } },
    ],
    tooltip: { valueSuffix: ' °C' },
  };

  const rainfallChartOpts = {
    ...chartBase(),
    chart: { ...chartBase().chart, type: 'column' },
    yAxis: yAxis('mm', { min: 0 }),
    series: [{ name: 'Rainfall', data: rainfallSeries, color: '#3aa0c9', borderRadius: 2 }],
    tooltip: { valueSuffix: ' mm' },
    plotOptions: { column: { pointPadding: 0.1, groupPadding: 0.1 } },
  };

  const humidityChartOpts = {
    ...chartBase(),
    chart: { ...chartBase().chart, type: 'line' },
    yAxis: yAxis('%', { min: 0, max: 100 }),
    series: [{ name: 'Humidity', data: humiditySeries, color: '#8e44ad', lineWidth: 2, marker: { radius: 3 } }],
    tooltip: { valueSuffix: ' %' },
  };

  const RISK_SHORT = { EMERGENCY: 'EMERG', 'HIGH RISK': 'HIGH', 'INTERMEDIATE RISK': 'INTER', NORMAL: 'NORMAL' };

  return (
    <div style={{ fontFamily: '"Segoe UI",system-ui,sans-serif', fontSize: 14, color: T.ink }}>

      {/* Camp name + subtitle */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: T.navy, marginBottom: 2 }}>{camp.name}</div>
        <div style={{ fontSize: 12.5, color: T.muted }}>{camp.adm2_name} · {camp.adm1_name}</div>
      </div>

      {/* Species */}
      {speciesObj && (() => {
        const imgCode = SPECIES_IMG[speciesObj.species_id];
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '8px 10px', border: `1px solid ${T.line}`, borderRadius: 8, background: T.panel }}>
            {imgCode && (
              <img src={`assets/images/species-${imgCode}.jpg`} alt={speciesObj.scientific_name}
                style={{ width: 54, height: 40, objectFit: 'cover', borderRadius: 5, flexShrink: 0 }} />
            )}
            <div>
              <div style={{ fontSize: 11, fontStyle: 'italic', color: T.ink, fontWeight: 600 }}>{speciesObj.scientific_name || speciesObj.species}</div>
              {speciesObj.common_name && <div style={{ fontSize: 10.5, color: T.navy2 }}>({speciesObj.common_name})</div>}
            </div>
          </div>
        );
      })()}

      {/* Risk badge — uses period risk (same as advisory) */}
      {(periodRiskLevel || conditions?.risk_level) && (() => {
        const rl = periodRiskLevel || conditions.risk_level;
        return (
          <div style={{ display: 'inline-block', fontWeight: 800, fontSize: 13, padding: '5px 14px', borderRadius: 5, color: RISK_TEXT[rl] || '#fff', background: riskColor(rl), marginBottom: 16 }}>
            {rl}
          </div>
        );
      })()}

      {/* Registered users */}
      <div style={{ marginBottom: 16 }}>
        <div style={S.blockLabel}>Registered Users</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={S.statCard}>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.navy }}>{(farmers_count ?? 0).toLocaleString()}</div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>Fish farmers</div>
          </div>
          <div style={S.statCard}>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.navy }}>{(officers_count ?? 0).toLocaleString()}</div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>Extension officers</div>
          </div>
        </div>
      </div>

      {/* Conditions meta-grid */}
      {conditions && (
        <div style={{ marginBottom: 16 }}>
          <div style={S.blockLabel}>Conditions</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' }}>
            <div><div style={S.metaKey}>Pond temp</div><div style={S.metaVal}>{conditions.predicted_pond_temp?.toFixed(1) ?? '–'}°C</div></div>
            <div><div style={S.metaKey}>Air (min–max)</div><div style={S.metaVal}>{conditions.temp_min?.toFixed(1) ?? '–'}–{conditions.temp_max?.toFixed(1) ?? '–'}°C</div></div>
            {conditions.weather_type && (
              <div style={{ gridColumn: '1 / -1' }}><div style={S.metaKey}>Weather</div><div style={S.metaVal}>{conditions.weather_type}</div></div>
            )}
          </div>
        </div>
      )}

      {/* Pond temp chart + risk outlook strip */}
      {ol.length > 0 && (
        <div style={S.chartCard}>
          <div style={S.chartTitle}>
            Pond Temperature <span style={{ fontWeight: 400, color: T.muted }}>°C</span>
          </div>
          <HighchartsReact highcharts={Highcharts} options={pondChartOpts} />

          {/* Risk strip */}
          <div style={S.blockLabel}>Risk Outlook</div>
          <div style={{ display: 'flex', gap: 5, overflowX: 'auto', paddingBottom: 4 }}>
            {ol.map((o, i) => {
              const rl = o.risk_level;
              const bg = riskColor(rl);
              const tx = RISK_TEXT[rl] || '#fff';
              return (
                <div key={i} style={{ background: bg, borderRadius: 6, padding: '5px 3px 4px', textAlign: 'center', lineHeight: 1.2, minWidth: 46, flexShrink: 0 }}
                  title={`${fmtDate(o.date)} · ${o.predicted_pond_temp?.toFixed(1) ?? '–'}°C`}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: tx, opacity: 0.9 }}>{fmtDate(o.date)}</div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: tx, margin: '1px 0' }}>{o.predicted_pond_temp?.toFixed(0) ?? '–'}°</div>
                  <div style={{ fontSize: 8, fontWeight: 800, color: tx, letterSpacing: '.03em' }}>{RISK_SHORT[rl] ?? (rl || '–')}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Air temp chart */}
      {ol.length > 0 && (
        <div style={S.chartCard}>
          <div style={S.chartTitle}>
            Air Temperature <span style={{ fontWeight: 400, color: T.muted }}>°C</span>
          </div>
          <HighchartsReact highcharts={Highcharts} options={{ ...airChartOpts, legend: { enabled: true, itemStyle: { fontSize: '10px', color: T.ink } } }} />
        </div>
      )}

      {/* Rainfall chart */}
      {hasRainfall && (
        <div style={S.chartCard}>
          <div style={S.chartTitle}>Rainfall <span style={{ fontWeight: 400, color: T.muted }}>mm</span></div>
          <HighchartsReact highcharts={Highcharts} options={rainfallChartOpts} />
        </div>
      )}

      {/* Humidity chart */}
      {hasHumidity && (
        <div style={S.chartCard}>
          <div style={S.chartTitle}>Humidity <span style={{ fontWeight: 400, color: T.muted }}>%</span></div>
          <HighchartsReact highcharts={Highcharts} options={humidityChartOpts} />
        </div>
      )}

    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────
function RiskMap() {
  const { logout } = useContext(AuthContext);

  // filters
  const [adm1List,    setAdm1List]    = useState([]);
  const [adm2List,    setAdm2List]    = useState([]);
  const [adm3List,    setAdm3List]    = useState([]);
  const [speciesList, setSpeciesList] = useState([]);
  const [selCountry,  setSelCountry]  = useState(2);
  const [selAdm1,     setSelAdm1]     = useState('');
  const [selAdm2,     setSelAdm2]     = useState('');
  const [selAdm3,     setSelAdm3]     = useState('');
  const [selSpecies,  setSelSpecies]  = useState('');
  const [dateOffset,  setDateOffset]  = useState(6);

  // map / right panel
  const [riskLevels,        setRiskLevels]         = useState([]);
  const [riskRecords,       setRiskRecords]        = useState([]);
  const [selectedCampInfo,  setSelectedCampInfo]   = useState(null);
  const [campDetail,        setCampDetail]          = useState(null);
  const [loadingDetail,     setLoadingDetail]       = useState(false);
  const [drillLabel,        setDrillLabel]          = useState('');
  const [dynamicRisk,       setDynamicRisk]         = useState(null);
  // appliedFilters drives all data fetches; set by Apply button, map click, or auto-init
  const [appliedFilters,    setAppliedFilters]       = useState(null);
  const [dataSource,        setDataSource]           = useState('vc');

  // loading states
  const [loadingMap,      setLoadingMap]      = useState(false);
  const [loadingDynamic,  setLoadingDynamic]  = useState(false);
  const [loadingAdm2,     setLoadingAdm2]     = useState(false);
  const [loadingAdm3,     setLoadingAdm3]     = useState(false);

  // advisory section (below map)
  const [advisoryText,      setAdvisoryText]        = useState('');
  const [advLang,           setAdvLang]             = useState('English');
  const [farmersList,       setFarmersList]         = useState([]);
  const [officersList,      setOfficersList]        = useState([]);
  const [farmersActive,     setFarmersActive]       = useState(true);
  const [farmersMode,       setFarmersMode]         = useState('all');
  const [farmersSelected,   setFarmersSelected]     = useState(new Set());
  const [officersActive,    setOfficersActive]      = useState(true);
  const [officersMode,      setOfficersMode]        = useState('all');
  const [officersSelected,  setOfficersSelected]    = useState(new Set());
  const [advisoryModal,     setAdvisoryModal]       = useState(null);
  const [sendingAdvisory,   setSendingAdvisory]     = useState(false);

  // leaflet refs
  const mapDivRef         = useRef(null);
  const mapRef            = useRef(null);
  const layerRef          = useRef(null);
  const pointLayerRef     = useRef(null);
  const maskLayerRef      = useRef(null);
  const geoCacheRef       = useRef({});
  const lastZoomedCampRef    = useRef(null);
  const shouldFitRef         = useRef(false);
  const userChangedAdm1Ref   = useRef(false);
  const userChangedAdm2Ref   = useRef(false);
  const handleResetRef       = useRef(null);

  // stable setters for leaflet callbacks
  const setSelAdm1Ref  = useRef(setSelAdm1);
  const setSelAdm2Ref  = useRef(setSelAdm2);
  const setSelAdm3Ref  = useRef(setSelAdm3);
  const setCampRef     = useRef(setSelectedCampInfo);
  const selCountryRef  = useRef(selCountry);
  useEffect(() => { selCountryRef.current = selCountry; }, [selCountry]);

  const selAdm1Ref      = useRef(selAdm1);
  const selAdm2Ref      = useRef(selAdm2);
  const selSpeciesRef   = useRef(selSpecies);
  const dateOffsetRef   = useRef(dateOffset);
  useEffect(() => { selAdm1Ref.current    = selAdm1;    }, [selAdm1]);
  useEffect(() => { selAdm2Ref.current    = selAdm2;    }, [selAdm2]);
  useEffect(() => { selSpeciesRef.current = selSpecies; }, [selSpecies]);
  useEffect(() => { dateOffsetRef.current = dateOffset; }, [dateOffset]);

  // stable ref for setAppliedFilters (used in Leaflet marker callbacks)
  const setAppliedFiltersRef = useRef(setAppliedFilters);

  // map-click cascade: specific adm2/adm3 to select when the lists load
  const pendingAdm2Ref  = useRef(null);
  const pendingAdm3Ref  = useRef(null);
  // suppress clearing selectedCampInfo during map-click-driven cascades
  const fromMapClickRef = useRef(false);

  // ── auth helper ─────────────────────────────────────────────────────────────
  const getHeaders = useCallback(() => {
    const token  = localStorage.getItem('profishBlue_authToken');
    const expiry = localStorage.getItem('profishBlue_tokenExpiry');
    if (!token || !expiry || Date.now() > parseInt(expiry, 10)) { logout(); return {}; }
    return { 'Content-Type': 'application/json', Authorization: token };
  }, [logout]);

  const apiFetch = useCallback(async (path) => {
    const res = await fetch(`${envConfig.API_URL}${path}`, { headers: getHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [getHeaders]);

  // ── init leaflet ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (mapRef.current || !mapDivRef.current) return;
    const map = L.map(mapDivRef.current, { center: [-13.1339, 27.8493], zoom: 6 });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(map);
    mapRef.current        = map;
    layerRef.current      = L.layerGroup().addTo(map);
    pointLayerRef.current = L.layerGroup().addTo(map);

    // Reset button as a Leaflet control (bottom-left)
    const ResetControl = L.Control.extend({
      options: { position: 'bottomleft' },
      onAdd() {
        const btn = L.DomUtil.create('button');
        btn.innerHTML = '&#8635; Reset';
        btn.title = 'Reset to full country view';
        btn.style.cssText = [
          'background:#fff', 'border:1px solid #b0bec5', 'border-radius:6px',
          'padding:5px 11px', 'font-size:12px', 'font-weight:600',
          'color:#546e7a', 'cursor:pointer', 'box-shadow:0 1px 4px rgba(0,0,0,.18)',
          'line-height:1.4', 'display:block',
        ].join(';');
        btn.onmouseover = () => { btn.style.background = '#eceff1'; };
        btn.onmouseout  = () => { btn.style.background = '#fff'; };
        L.DomEvent.on(btn, 'click', (e) => {
          L.DomEvent.stopPropagation(e);
          if (handleResetRef.current) handleResetRef.current();
        });
        return btn;
      },
    });
    new ResetControl().addTo(map);

    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // ── fetch risk levels on mount ───────────────────────────────────────────────
  useEffect(() => {
    apiFetch('/riskmap/risk-levels')
      .then(d => setRiskLevels(d.data || []))
      .catch(() => {});
  }, [apiFetch]);

  // ── country change: reset drilldown state and pan map ───────────────────────
  // adm1 list + species are fetched by the init effect on mount
  useEffect(() => {
    if (!selCountry) return;
    setSelAdm1(''); setSelAdm2(''); setSelAdm3('');
    setAdm2List([]); setAdm3List([]);
    setCampDetail(null); setSelectedCampInfo(null); setDynamicRisk(null); setDrillLabel('');
    setAppliedFilters(null); setRiskRecords([]);
    setAdvLang('English');
    if (mapRef.current && COUNTRY_VIEW[selCountry]) {
      const { center, zoom } = COUNTRY_VIEW[selCountry];
      mapRef.current.setView(center, zoom);
    }
  }, [selCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  const randPick = (arr) => arr[Math.floor(Math.random() * arr.length)];

  // ── adm1 change: fetch adm2 options only on user/map-click interaction ───────
  // guarded so the init async cascade can set selAdm1 without triggering a
  // conflicting secondary fetch
  useEffect(() => {
    if (!userChangedAdm1Ref.current && !fromMapClickRef.current) return;
    userChangedAdm1Ref.current = false;
    setSelAdm2(''); setSelAdm3(''); setAdm2List([]); setAdm3List([]);
    setLoadingAdm2(false); setLoadingAdm3(false);
    if (!selAdm1) return;
    setLoadingAdm2(true);
    apiFetch(`/lookups/locations/adm2?country_id=${selCountry}&adm1_id=${selAdm1}`)
      .then(d => setAdm2List(d.data || []))
      .catch(() => {})
      .finally(() => setLoadingAdm2(false));
  }, [selAdm1, selCountry, apiFetch]);

  // ── adm2 change: fetch adm3 options only on user/map-click interaction ───────
  useEffect(() => {
    if (!userChangedAdm2Ref.current && !fromMapClickRef.current) return;
    userChangedAdm2Ref.current = false;
    setSelAdm3(''); setAdm3List([]);
    setLoadingAdm3(false);
    if (!selAdm2) return;
    setLoadingAdm3(true);
    apiFetch(`/lookups/locations/adm3?country_id=${selCountry}&adm1_id=${selAdm1}&adm2_id=${selAdm2}`)
      .then(d => setAdm3List(d.data || []))
      .catch(() => {})
      .finally(() => setLoadingAdm3(false));
  }, [selAdm2, selAdm1, selCountry, apiFetch]);

  // ── map-click cascade: set pending adm2 when its list loads ─────────────────
  useEffect(() => {
    if (!adm2List.length || pendingAdm2Ref.current === null) return;
    const target = String(pendingAdm2Ref.current);
    pendingAdm2Ref.current = null;
    setSelAdm2(target);
    // fromMapClickRef stays true so adm2 change effect fires the adm3 fetch
  }, [adm2List]);

  // ── map-click cascade: set pending adm3 when its list loads ─────────────────
  useEffect(() => {
    if (!adm3List.length || pendingAdm3Ref.current === null) return;
    const targetId = pendingAdm3Ref.current;
    pendingAdm3Ref.current = null;
    fromMapClickRef.current = false;
    const match = adm3List.find(a => String(a.adm3_id) === String(targetId)) || adm3List[0];
    setSelAdm3(String(match.adm3_id));
    setCampRef.current({ campId: match.adm3_id, countryId: selCountry });
  }, [adm3List, selCountry]);

  // ── one-time mount init: fetch all levels sequentially, pick a random camp ───
  // single async waterfall avoids the apiFetch reference-change race that
  // caused the inter-effect cascade to stop after province selection
  useEffect(() => {
    const init = async () => {
      try {
        const [a1Res, spRes] = await Promise.all([
          apiFetch(`/lookups/locations/adm1?country_id=2`),
          apiFetch(`/lookups/fishes/species?country_id=2`),
        ]);
        const a1List = a1Res.data || [];
        setAdm1List(a1List);
        setSpeciesList(spRes.data || []);
        if (!a1List.length) return;

        const ra1    = randPick(a1List);
        const a2Res  = await apiFetch(`/lookups/locations/adm2?country_id=2&adm1_id=${ra1.adm1_id}`);
        const a2List = a2Res.data || [];
        setAdm2List(a2List);
        if (!a2List.length) return;

        const ra2    = randPick(a2List);
        const a3Res  = await apiFetch(`/lookups/locations/adm3?country_id=2&adm1_id=${ra1.adm1_id}&adm2_id=${ra2.adm2_id}`);
        const a3List = a3Res.data || [];
        setAdm3List(a3List);
        if (!a3List.length) return;

        const ra3 = randPick(a3List);
        // Set selections AFTER all data is ready — adm1/adm2 change effects are
        // guarded by userChangedAdm1Ref/userChangedAdm2Ref so they won't interfere
        setSelAdm1(String(ra1.adm1_id));
        setSelAdm2(String(ra2.adm2_id));
        setSelAdm3(String(ra3.adm3_id));
        setSelectedCampInfo({ campId: ra3.adm3_id, countryId: 2 });
        setAppliedFilters({
          country: 2,
          adm1: String(ra1.adm1_id),
          adm2: String(ra2.adm2_id),
          adm3: ra3.adm3_id,
          species: '',
          dateOffset: 6,
        });
      } catch (_) {}
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── fetch dynamic risk — fires on camp/Apply change or slider move ───────────
  useEffect(() => {
    if (!selectedCampInfo) { setDynamicRisk(null); setLoadingDynamic(false); return; }
    const { campId, countryId } = selectedCampInfo;
    const dOff    = appliedFilters?.dateOffset ?? 6;
    const species = appliedFilters?.species   || '';
    const hour = countryId === 2 ? 1 : DEFAULT_HOUR;
    const days = dOff + 1;
    const params = new URLSearchParams({ country_id: countryId, adm3_id: campId, days, hour, source: dataSource });
    if (species) params.set('species_id', species);
    setLoadingDynamic(true);
    apiFetch(`/riskmap/dynamic-risk?${params}`)
      .then(d => setDynamicRisk(d.data || null))
      .catch(() => setDynamicRisk(null))
      .finally(() => setLoadingDynamic(false));
  }, [selectedCampInfo, appliedFilters, dataSource, apiFetch]);

  // ── fetch risk data — fires only when appliedFilters changes (Apply button) ──
  useEffect(() => {
    if (!appliedFilters?.country) return;
    const { country, adm1, adm2, species, dateOffset: dOff = 6 } = appliedFilters;
    const hour = country === 2 ? 1 : DEFAULT_HOUR;
    const d = new Date(); d.setDate(d.getDate() + dOff);
    const date = d.toISOString().split('T')[0];
    const params = new URLSearchParams({ country_id: country, date, hour, source: dataSource });
    if (species) params.set('species_id', species);
    if (adm1)    params.set('adm1_id', adm1);
    if (adm2)    params.set('adm2_id', adm2);
    setLoadingMap(true);
    apiFetch(`/riskmap/risk?${params}`)
      .then(d => setRiskRecords(d.data || []))
      .catch(() => setRiskRecords([]))
      .finally(() => setLoadingMap(false));
  }, [appliedFilters, dataSource, apiFetch]);

  // ── update map layers when risk data or drilldown changes ───────────────────
  useEffect(() => {
    if (!mapRef.current || !layerRef.current || !pointLayerRef.current || !selCountry) return;

    const iso3      = selCountry === 1 ? 'mwi' : 'zmb';
    const polyLevel = selAdm1 ? 2 : 1;
    const riskOrder = riskLevels.map(l => l.weekly_risk_level);
    const adm3Risk  = {};
    const adm2Risk  = worstRiskFromRecords(riskRecords, 'adm2_id', riskOrder);
    const adm1Risk  = worstRiskFromRecords(riskRecords, 'adm1_id', riskOrder);
    for (const r of riskRecords) adm3Risk[r.adm3_id] = r.risk_level;

    // ── polygon layer ──────────────────────────────────────────────────────────
    // Rebuild the darken-outside mask on every polygon redraw:
    //   polyLevel=1 → holes = all adm1 polygons (darken outside Zambia)
    //   polyLevel=2 → holes = selected adm1 polygon only (darken outside province)
    const buildDonutMask = (geoData) => {
      if (maskLayerRef.current) { maskLayerRef.current.remove(); maskLayerRef.current = null; }
      let holeFeatures;
      if (polyLevel === 1) {
        holeFeatures = geoData.features || [];
      } else {
        const adm1Cache = geoCacheRef.current[`${iso3}_1`];
        if (!adm1Cache) return;
        holeFeatures = (adm1Cache.features || []).filter(
          f => String(f.properties.profishblue_id) === String(selAdm1)
        );
      }
      const worldRing = [[-180, 90], [180, 90], [180, -90], [-180, -90], [-180, 90]];
      const holes = [];
      holeFeatures.forEach(feat => {
        const geom = feat.geometry;
        if (!geom) return;
        if (geom.type === 'Polygon') holes.push(geom.coordinates[0]);
        else if (geom.type === 'MultiPolygon') geom.coordinates.forEach(p => holes.push(p[0]));
      });
      if (!holes.length) return;
      const donut = { type: 'Feature', geometry: { type: 'Polygon', coordinates: [worldRing, ...holes] } };
      maskLayerRef.current = L.geoJSON(donut, {
        style: { fillColor: '#546e7a', fillOpacity: 0.5, color: 'none', fillRule: 'evenodd', interactive: false },
      }).addTo(mapRef.current);
      maskLayerRef.current.bringToBack();
    };

    const renderPolygons = (geoData) => {
      buildDonutMask(geoData);
      layerRef.current.clearLayers();
      const lyr = L.geoJSON(geoData, {
        style: (feat) => {
          const id         = feat.properties.profishblue_id;
          const isSelected = polyLevel === 2 && selAdm2 && String(id) === String(selAdm2);
          return {
            color:       isSelected ? '#37474f' : '#90a4ae',
            weight:      isSelected ? 2 : 1,
            fillColor:   '#ffffff',
            fillOpacity: isSelected ? 0.15 : 0.04,
          };
        },
        onEachFeature: (feat, lyr) => {
          const id   = feat.properties.profishblue_id;
          const name = feat.properties.name || `ID ${id}`;
          lyr.bindTooltip(`<b>${name}</b>`, { sticky: true });
          lyr.on('click', () => {
            mapRef.current.fitBounds(lyr.getBounds(), { padding: [20, 20] });
            if (polyLevel === 1) {
              userChangedAdm1Ref.current = true;
              setSelAdm1Ref.current(String(id));
              setDrillLabel(name);
            } else {
              userChangedAdm2Ref.current = true;
              setSelAdm2Ref.current(String(id));
              setDrillLabel(name);
            }
          });
        },
      }).addTo(layerRef.current);
      if (shouldFitRef.current && !selectedCampInfo) {
        shouldFitRef.current = false;
        try { mapRef.current.fitBounds(lyr.getBounds(), { padding: [30, 30] }); } catch (_) {}
      }
    };

    // ── camp point layer (visible whenever adm1 is selected) ──────────────────
    const selectedCampId = selectedCampInfo ? String(selectedCampInfo.campId) : null;
    const renderPoints = (geoData) => {
      pointLayerRef.current.clearLayers();
      (geoData.features || []).forEach(feat => {
        const { profishblue_id, name, latitude, longitude, adm1_id, adm2_id } = feat.properties;
        const rl         = adm3Risk[profishblue_id];
        const color      = riskColor(rl);
        const isSelected = selectedCampId === String(profishblue_id);
        const marker = L.circleMarker([latitude, longitude], {
          radius:      isSelected ? 11 : 7,
          color:       isSelected ? '#37474f' : '#fff',
          weight:      isSelected ? 2.5 : 1.5,
          fillColor:   color,
          fillOpacity: isSelected ? 1.0 : 0.85,
        });
        marker.bindTooltip(`<b>${name}</b>`, { sticky: true });
        marker.on('click', () => {
          const country = selCountryRef.current;
          mapRef.current.flyTo([latitude, longitude], 11, { duration: 1 });
          lastZoomedCampRef.current = profishblue_id;
          // Immediately push camp + filters so the right panel and risk data update
          setCampRef.current({ campId: profishblue_id, countryId: country });
          setAppliedFiltersRef.current({
            country,
            adm1:       String(adm1_id),
            adm2:       String(adm2_id),
            adm3:       profishblue_id,
            species:    selSpeciesRef.current,
            dateOffset: dateOffsetRef.current,
          });

          const curAdm1 = String(selAdm1Ref.current);
          const curAdm2 = String(selAdm2Ref.current);

          if (curAdm2 === String(adm2_id)) {
            // Same district — sync camp dropdown only
            setSelAdm3Ref.current(String(profishblue_id));
          } else if (curAdm1 === String(adm1_id)) {
            // Same province, different district — cascade from adm2
            fromMapClickRef.current = true;
            pendingAdm3Ref.current  = profishblue_id;
            setSelAdm2Ref.current(String(adm2_id));
          } else {
            // Different province — full cascade from adm1
            fromMapClickRef.current = true;
            pendingAdm2Ref.current  = adm2_id;
            pendingAdm3Ref.current  = profishblue_id;
            setSelAdm1Ref.current(String(adm1_id));
          }
        });
        marker.addTo(pointLayerRef.current);
      });
    };

    // Load polygon GeoJSON (cached)
    const polyKey = `${iso3}_${polyLevel}`;
    const loadPolygons = (cb) => {
      if (geoCacheRef.current[polyKey]) { cb(geoCacheRef.current[polyKey]); return; }
      apiFetch(`/riskmap/geojson?country_id=${selCountry}&level=${polyLevel}`)
        .then(geo => { geoCacheRef.current[polyKey] = geo; cb(geo); })
        .catch(() => {});
    };

    // Point scope follows appliedFilters — before Apply all country camps show grey
    const ptAdm1   = appliedFilters?.adm1 || '';
    const ptAdm2   = appliedFilters?.adm2 || '';
    const ptKey    = ptAdm2 ? `pts_adm2_${ptAdm2}`
                   : ptAdm1 ? `pts_adm1_${ptAdm1}`
                   :          `pts_country_${selCountry}`;
    const ptParams = new URLSearchParams({ country_id: selCountry, level: 3 });
    if (ptAdm2)      ptParams.set('adm2_id', ptAdm2);
    else if (ptAdm1) ptParams.set('adm1_id', ptAdm1);
    const loadPoints = (cb) => {
      if (geoCacheRef.current[ptKey]) { cb(geoCacheRef.current[ptKey]); return; }
      apiFetch(`/riskmap/geojson?${ptParams}`)
        .then(geo => { geoCacheRef.current[ptKey] = geo; cb(geo); })
        .catch(() => {});
    };

    loadPolygons(renderPolygons);
    loadPoints(renderPoints);
  }, [selCountry, selAdm1, selAdm2, appliedFilters, riskRecords, riskLevels, selectedCampInfo, apiFetch]);

  // ── fetch camp detail — fires on camp change; Apply refreshes via selectedCampInfo ──
  useEffect(() => {
    if (!selectedCampInfo) return;
    setLoadingDetail(true);
    setCampDetail(null);
    const { campId, countryId } = selectedCampInfo;
    const dOff    = appliedFilters?.dateOffset ?? 6;
    const species = appliedFilters?.species   || '';
    const hour = countryId === 2 ? 1 : DEFAULT_HOUR;
    const today = new Date().toISOString().split('T')[0];
    const params = new URLSearchParams({ country_id: countryId, adm3_id: campId, date: today, hour, days: dOff + 1, source: dataSource });
    if (species) params.set('species_id', species);
    apiFetch(`/riskmap/camp?${params}`)
      .then(d => setCampDetail(d.data || null))
      .catch(() => setCampDetail(null))
      .finally(() => setLoadingDetail(false));
  }, [selectedCampInfo, appliedFilters, dataSource, apiFetch]);

  // ── zoom to camp when campDetail loads for a new camp ────────────────────────
  useEffect(() => {
    if (!campDetail?.camp?.latitude || !mapRef.current) return;
    const campId = campDetail.camp.id;
    if (lastZoomedCampRef.current === campId) return;
    lastZoomedCampRef.current = campId;
    mapRef.current.flyTo([campDetail.camp.latitude, campDetail.camp.longitude], 11, { duration: 1.2 });
  }, [campDetail]);

  // ── advisory text: driven by dynamic risk (slider-based) ────────────────────
  // Malawi: English + Chichewa.  Zambia: English + Bemba + Nyanja (chichewa_zmb).
  const advLangOptions = selCountry === 1
    ? [{ label: 'English', key: 'english' }, { label: 'Chichewa', key: 'chichewa' }]
    : [{ label: 'English', key: 'english' }, { label: 'Bemba', key: 'bemba' }, { label: 'Chichewa', key: 'chichewa_zmb' }];

  useEffect(() => {
    if (!dynamicRisk?.advisory) { setAdvisoryText(''); return; }
    const opt = advLangOptions.find(o => o.label === advLang) || advLangOptions[0];
    setAdvisoryText(dynamicRisk.advisory[opt.key] || '');
  }, [dynamicRisk, advLang, selCountry]);

  // ── fetch recipients when camp selected ─────────────────────────────────────
  useEffect(() => {
    if (!selectedCampInfo) {
      setFarmersList([]); setOfficersList([]);
      setFarmersSelected(new Set()); setFarmersMode('all'); setFarmersActive(true);
      setOfficersSelected(new Set()); setOfficersMode('all'); setOfficersActive(true);
      return;
    }
    const { campId, countryId } = selectedCampInfo;
    const headers = getHeaders();
    Promise.all([
      fetch(`${envConfig.API_URL}/fishermen?country_id=${countryId}&adm3_id=${campId}`, { headers }),
      fetch(`${envConfig.API_URL}/extoff?country_id=${countryId}&adm3_id=${campId}`, { headers }),
    ]).then(async ([fRes, oRes]) => {
      const fData = await fRes.json().catch(() => ({}));
      const oData = await oRes.json().catch(() => ({}));
      setFarmersList((fData.data || []).map(f => ({ id: f.fisher_id, name: f.name, phone: f.phone })));
      setOfficersList((oData.data || []).map(o => ({ id: o.fisher_id, name: o.name, phone: o.phone, role: o.designation || 'Extension Officer' })));
      setFarmersSelected(new Set()); setFarmersMode('all'); setFarmersActive(true);
      setOfficersSelected(new Set()); setOfficersMode('all'); setOfficersActive(true);
    }).catch(() => { setFarmersList([]); setOfficersList([]); });
  }, [selectedCampInfo, getHeaders]);

  // ── send advisory ─────────────────────────────────────────────────────────
  const sendAdvisory = async () => {
    if (!campDetail || !selectedCampInfo) return;
    setSendingAdvisory(true);
    const { campId, countryId } = selectedCampInfo;
    const camp = campDetail.camp;
    const cond = campDetail.conditions;
    const payload = {
      country_id: countryId,
      adm3_id: campId,
      adm2_id: camp.adm2_id,
      adm1_id: camp.adm1_id,
      predicted_pond_temperature: cond?.predicted_pond_temp,
      risk_level: cond?.risk_level,
      temp_max: cond?.temp_max,
      temp_min: cond?.temp_min,
      advisory_text: advisoryText,
      ref_date: selDate,
      farmers_active: farmersActive,
      extoff_active: officersActive,
      farmer_ids: farmersMode === 'select' && farmersSelected.size > 0 ? [...farmersSelected] : null,
      extoff_ids: officersMode === 'select' && officersSelected.size > 0 ? [...officersSelected] : null,
    };
    try {
      const token = localStorage.getItem('profishBlue_authToken');
      const res = await fetch(`${envConfig.API_URL}/advisory/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify(payload),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.message || `HTTP ${res.status}`);
      Swal.fire({ title: 'Message', text: result.message || 'Advisory sent successfully.', icon: 'success', confirmButtonText: 'OK' });
    } catch (e) {
      Swal.fire({ title: 'Error', text: e.message || 'Error sending advisory.', icon: 'error', confirmButtonText: 'OK' });
    } finally {
      setSendingAdvisory(false);
    }
  };

  // ── handlers ────────────────────────────────────────────────────────────────
  const handleAdm1Change = (e) => { userChangedAdm1Ref.current = true; setSelAdm1(e.target.value); setDrillLabel(''); };
  const handleAdm2Change = (e) => { userChangedAdm2Ref.current = true; setSelAdm2(e.target.value); setDrillLabel(''); };
  const handleDrillUp    = () => {
    if (selAdm2) { setSelAdm2(''); setAdm3List([]); setDrillLabel(''); }
    else if (selAdm1) { setSelAdm1(''); setSelAdm2(''); setAdm2List([]); setAdm3List([]); setDrillLabel(''); }
  };

  const handleApply = useCallback(() => {
    const filters = {
      country: selCountry, adm1: selAdm1, adm2: selAdm2, adm3: selAdm3,
      species: selSpecies, dateOffset,
    };
    setAppliedFilters(filters);
    if (selAdm3) {
      setSelectedCampInfo({ campId: +selAdm3, countryId: selCountry });
    } else {
      shouldFitRef.current = true;
      setSelectedCampInfo(null); setCampDetail(null); setDynamicRisk(null);
    }
  }, [selCountry, selAdm1, selAdm2, selAdm3, selSpecies, dateOffset]);

  const handleReset = useCallback(() => {
    setSelAdm1(''); setSelAdm2(''); setSelAdm3('');
    setAdm2List([]); setAdm3List([]);
    setRiskRecords([]); setSelectedCampInfo(null); setCampDetail(null);
    setDynamicRisk(null); setDrillLabel(''); setAppliedFilters(null);
    if (mapRef.current && COUNTRY_VIEW[selCountry]) {
      const { center, zoom } = COUNTRY_VIEW[selCountry];
      mapRef.current.setView(center, zoom);
    }
  }, [selCountry]);
  handleResetRef.current = handleReset;

  // ── render ───────────────────────────────────────────────────────────────────
  const selDate = (() => { const d = new Date(); d.setDate(d.getDate() + dateOffset); return d.toISOString().split('T')[0]; })();
  const totalAdvisoryRecipients =
    (farmersActive ? (farmersMode === 'all' ? farmersList.length : farmersSelected.size) : 0) +
    (officersActive ? (officersMode === 'all' ? officersList.length : officersSelected.size) : 0);

  return (
    <div className="wrapper pt-100px" style={{ height: '100vh', overflow: 'hidden' }}>
      <div className="container-fluid" style={{ height: 'calc(100vh - 100px)', display: 'flex', padding: '12px 0 0 0' }}>

          {/* ── Left: filters ── */}
          <div style={{ width: '210px', minWidth: '210px', height: '100%', display: 'flex', flexDirection: 'column', borderRight: `1px solid ${T.line}`, background: T.panel }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 8px' }}>

              {/* Location */}
              <div style={{ marginBottom: 14 }}>
                <div style={S.blockLabel}>Location</div>
                {/* Country — fixed to Zambia */}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: T.muted, marginBottom: 3 }}>Country</div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, padding: '5px 8px', borderRadius: 5, border: `1px solid ${T.line}`, background: T.field }}>Zambia</div>
                </div>
                {[
                  { label: 'Province', el: (
                    <select style={{ width: '100%', fontSize: 12.5, padding: '5px 8px', borderRadius: 5, border: `1px solid ${T.line}`, background: selCountry ? '#fff' : T.field, color: T.ink }}
                      value={selAdm1} onChange={handleAdm1Change} disabled={!selCountry}>
                      <option value="">Select</option>
                      {adm1List.map(a => <option key={a.adm1_id} value={a.adm1_id}>{a.adm1}</option>)}
                    </select>
                  )},
                  { label: selCountry === 1 ? 'District' : 'District', spinner: loadingAdm2, el: (
                    <select style={{ width: '100%', fontSize: 12.5, padding: '5px 8px', borderRadius: 5, border: `1px solid ${T.line}`, background: (!selAdm1 || loadingAdm2) ? T.field : '#fff', color: T.ink }}
                      value={selAdm2} onChange={handleAdm2Change} disabled={!selAdm1 || loadingAdm2}>
                      <option value="">{loadingAdm2 ? 'Loading…' : 'Select'}</option>
                      {adm2List.map(a => <option key={a.adm2_id} value={a.adm2_id}>{a.adm2}</option>)}
                    </select>
                  )},
                  { label: selCountry === 1 ? 'EPA' : 'Camp', spinner: loadingAdm3, el: (
                    <select style={{ width: '100%', fontSize: 12.5, padding: '5px 8px', borderRadius: 5, border: `1px solid ${T.line}`, background: (!selAdm2 || loadingAdm3) ? T.field : '#fff', color: T.ink }}
                      value={selAdm3} onChange={e => setSelAdm3(e.target.value)} disabled={!selAdm2 || loadingAdm3}>
                      <option value="">{loadingAdm3 ? 'Loading…' : 'Select'}</option>
                      {adm3List.map(a => <option key={a.adm3_id} value={a.adm3_id}>{a.adm3}</option>)}
                    </select>
                  )},
                ].map(({ label, required, spinner, el }) => (
                  <div key={label} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {label}{required && <span style={{ color: '#e05' }}>*</span>}
                      {spinner && <span className="spinner-border spinner-border-sm" style={{ width: '0.6rem', height: '0.6rem', borderWidth: '0.12em' }} />}
                    </div>
                    {el}
                  </div>
                ))}
              </div>

              {/* Species */}
              <div style={{ marginBottom: 14 }}>
                <div style={S.blockLabel}>Species</div>
                <select style={{ width: '100%', fontSize: 12.5, padding: '5px 8px', borderRadius: 5, border: `1px solid ${T.line}`, background: selCountry ? '#fff' : T.field, color: T.ink }}
                  value={selSpecies} onChange={e => setSelSpecies(e.target.value)} disabled={!selCountry}>
                  <option value="">All species</option>
                  {speciesList.map(s => <option key={s.species_id} value={s.species_id}>{s.scientific_name || s.species}</option>)}
                </select>
              </div>

              {/* Forecast window */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ ...S.blockLabel, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Forecast Window</span>
                  <span style={{ fontWeight: 500, color: T.navy, textTransform: 'none', fontSize: 11 }}>{dateOffset + 1}d</span>
                </div>
                <input type="range" className="form-range" min={2} max={6} step={1}
                  value={dateOffset} onChange={e => setDateOffset(+e.target.value)} style={{ accentColor: T.navy2 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: T.muted, marginTop: 1 }}>
                  <span>1d</span>
                  <span>{fmtDate(offsetDate(0))} → {fmtDate(selDate)}</span>
                  <span>7d</span>
                </div>
              </div>

              {/* Data source toggle */}
              <div style={{ marginBottom: 14 }}>
                <div style={S.blockLabel}>Data Source</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {DATA_SOURCES.map(ds => (
                    <label key={ds.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer', color: T.ink }}>
                      <input type="radio" name="datasource" value={ds.key} checked={dataSource === ds.key}
                        onChange={() => setDataSource(ds.key)} style={{ accentColor: T.navy2 }} />
                      {ds.label}
                    </label>
                  ))}
                </div>
              </div>

            </div>
            {/* Apply button */}
            <div style={{ flexShrink: 0, padding: '10px 12px', borderTop: `1px solid ${T.line}`, background: T.panel }}>
              <button onClick={handleApply}
                style={{ width: '100%', padding: '8px 0', background: T.navy2, color: '#fff', fontWeight: 700, fontSize: 13, border: 'none', borderRadius: 6, cursor: 'pointer', transition: 'background .15s' }}>
                Apply
              </button>
            </div>
          </div>

          {/* ── Center: map + advisory ── */}
          <div style={{ flex: 1, height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '0 10px' }}>
            {/* Leaflet map */}
            <div ref={mapDivRef} style={{ height: '440px', flexShrink: 0, position: 'relative', borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.line}`, background: '#eaf1f4' }}>
              {/* Map loading overlay */}
              {loadingMap && (
                <div style={{
                  position: 'absolute', inset: 0, zIndex: 1100,
                  background: 'rgba(255,255,255,0.55)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div className="spinner-border text-success" style={{ width: '2rem', height: '2rem' }} />
                </div>
              )}
              {/* Data source badge — top-left overlay */}
              <div style={{
                position: 'absolute', top: 10, left: 10, zIndex: 1000,
                background: 'rgba(255,255,255,0.92)', borderRadius: 5,
                padding: '3px 8px', boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                fontSize: '0.68rem', fontWeight: 700, color: T.navy2,
                display: 'flex', alignItems: 'center', gap: 4, pointerEvents: 'none',
              }}>
                <i className="bi bi-database" style={{ fontSize: 11 }} />
                {DATA_SOURCES.find(d => d.key === dataSource)?.label}
              </div>

              {/* Map legend — bottom-right overlay */}
              {riskLevels.length > 0 && (
                <div style={{
                  position: 'absolute', bottom: 24, right: 10, zIndex: 1000,
                  background: 'rgba(255,255,255,0.92)', borderRadius: 6,
                  padding: '8px 10px', boxShadow: '0 1px 5px rgba(0,0,0,0.25)',
                  fontSize: '0.75rem', pointerEvents: 'none',
                }}>
                  <div className="fw-semibold text-uppercase mb-1" style={{ fontSize: '0.68rem', color: '#555', letterSpacing: '0.04em' }}>Risk Level</div>
                  {riskLevels.map(l => (
                    <div key={l.id} className="d-flex align-items-center gap-1 mb-1">
                      <div style={{ width: 13, height: 13, borderRadius: 3, background: riskColor(l.weekly_risk_level), flexShrink: 0 }} />
                      <span style={{ textTransform: 'capitalize' }}>{l.weekly_risk_level.charAt(0) + l.weekly_risk_level.slice(1).toLowerCase()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Advisory section (below map) ── */}
            {(campDetail || loadingDetail) && (
              <div style={{ padding: '12px 0 16px' }}>
                <div style={{ border: `1px solid ${T.line}`, borderRadius: 10, background: '#fff', padding: '16px 18px', position: 'relative' }}>
                  {/* Section header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.navy }}>
                      {dynamicRisk?.period_label ? `${dynamicRisk.period_label} Advisory` : 'Advisory'}
                    </div>
                    {dynamicRisk?.risk_level && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 800, fontSize: 12, padding: '3px 10px', borderRadius: 4,
                          color: RISK_TEXT[dynamicRisk.risk_level] || '#fff', background: riskColor(dynamicRisk.risk_level) }}>
                          {dynamicRisk.risk_level}
                        </span>
                        <span style={{ fontSize: 11.5, color: T.muted }}>
                          {fmtDate(dynamicRisk.date_from)} → {fmtDate(dynamicRisk.date_to)}
                        </span>
                        {dynamicRisk.pond_temp_min != null && (
                          <span style={{ fontSize: 11, color: T.muted }}>
                            Pond {dynamicRisk.pond_temp_min}–{dynamicRisk.pond_temp_max}°C
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Advisory loading overlay */}
                  {loadingDynamic && (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'rgba(255,255,255,0.65)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10 }}>
                      <div className="spinner-border" style={{ color: T.navy2, width: '1.4rem', height: '1.4rem' }} />
                    </div>
                  )}

                  {/* Language + send row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 10 }}>
                    <div style={{ display: 'flex', gap: 14, flex: 1, flexWrap: 'wrap' }}>
                      {advLangOptions.map(({ label }) => (
                        <label key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, cursor: 'pointer', color: T.ink }}>
                          <input type="radio" name="advlang" value={label} checked={advLang === label}
                            onChange={() => setAdvLang(label)} disabled={!dynamicRisk?.advisory}
                            style={{ accentColor: T.navy2 }} />
                          {label}
                        </label>
                      ))}
                    </div>
                    <span style={{ fontSize: 11.5, color: T.muted, fontStyle: 'italic', whiteSpace: 'nowrap' }}
                      title="Advisory sending is available on the Home page">
                      Send Advisory ↗ Home
                    </span>
                  </div>

                  {/* Advisory textarea */}
                  <textarea
                    style={{ whiteSpace: 'pre-line', width: '100%', border: `1px solid ${T.line}`, resize: 'vertical',
                      background: T.panel, fontSize: 13.5, fontWeight: 400, color: '#c0392f',
                      borderRadius: 6, padding: '10px 12px', fontFamily: 'inherit' }}
                    rows={4}
                    value={advisoryText}
                    onChange={e => setAdvisoryText(e.target.value)}
                    placeholder={dynamicRisk?.risk_level ? '' : 'No advisory data for selected forecast window.'}
                  />

                  {/* Recipients */}
                  <div style={{ marginTop: 14 }}>
                    <div style={S.blockLabel}>Recipients</div>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
                      <RecipientGroupCard
                        icon="🐟" title="Fish Farmers" subtitle="Registered pond operators in this camp"
                        mode={farmersMode} setMode={setFarmersMode}
                        count={farmersSelected.size} total={farmersList.length}
                        active={farmersActive} setActive={setFarmersActive}
                        onOpenPicker={() => setAdvisoryModal('farmers')}
                      />
                      <RecipientGroupCard
                        icon="🧑‍🌾" title="Extension Officers" subtitle="District & field support staff"
                        mode={officersMode} setMode={setOfficersMode}
                        count={officersSelected.size} total={officersList.length}
                        active={officersActive} setActive={setOfficersActive}
                        onOpenPicker={() => setAdvisoryModal('officers')}
                      />
                    </div>
                    <p style={{ marginTop: 8, marginBottom: 0, fontSize: 12.5, color: T.muted }}>
                      <strong style={{ color: T.ink }}>{totalAdvisoryRecipients}</strong> recipient{totalAdvisoryRecipients !== 1 ? 's' : ''} will receive this advisory
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Right: camp detail ── */}
          <div style={{ width: '340px', minWidth: '300px', height: '100%', overflowY: 'auto', borderLeft: `1px solid ${T.line}`, background: '#fff' }}>
            <div style={{ padding: '14px 14px 20px' }}>
              <div style={{ ...S.blockLabel, marginBottom: 12 }}>
                {selectedCampInfo ? 'Camp Details' : 'Details'}
              </div>
              <CampPanel detail={campDetail} loading={loadingDetail} periodRiskLevel={dynamicRisk?.risk_level} />
            </div>
          </div>

      </div>

      {/* ── Recipient modals ── */}
      {advisoryModal === 'farmers' && (
        <RecipientModal
          title="Select fish farmers"
          people={farmersList}
          selectedIds={farmersSelected}
          onToggle={id => { const n = new Set(farmersSelected); n.has(id) ? n.delete(id) : n.add(id); setFarmersSelected(n); }}
          onSelectAll={ids => setFarmersSelected(new Set([...farmersSelected, ...ids]))}
          onClear={() => setFarmersSelected(new Set())}
          onClose={() => setAdvisoryModal(null)}
          onDone={() => { setFarmersMode('select'); setAdvisoryModal(null); }}
          fields="farmers"
        />
      )}
      {advisoryModal === 'officers' && (
        <RecipientModal
          title="Select extension officers"
          people={officersList}
          selectedIds={officersSelected}
          onToggle={id => { const n = new Set(officersSelected); n.has(id) ? n.delete(id) : n.add(id); setOfficersSelected(n); }}
          onSelectAll={ids => setOfficersSelected(new Set([...officersSelected, ...ids]))}
          onClear={() => setOfficersSelected(new Set())}
          onClose={() => setAdvisoryModal(null)}
          onDone={() => { setOfficersMode('select'); setAdvisoryModal(null); }}
          fields="officers"
        />
      )}

      <Footer />
    </div>
  );
}

export default RiskMap;
